import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AsyncThunk } from '@reduxjs/toolkit';
import CandidatesService from '../../services/candidates/candidates.service';
import ConsolidationService from '../../services/consolidation/consolidation.service';
import { mapCandidateItemToCandidateItemResponse } from '../candidates/candidates.mappers';
import type { CandidateItem, CandidatesState } from '../candidates/candidates.types';
import {
  compareSeveralCandidatesItemsMapper,
  mapCandidateItemResponseToMap,
  normalizeCandidatePreofferInfoInCandidateItemResponse,
  normalizeDatesInCandidateItemResponse,
  possibleDuplicatesMapper,
} from './consolidation.mappers';
import type { DiffState } from './consolidation.types';

type GetPossibleDuplicatesState = {
  candidates: Pick<CandidatesState, 'candidatesConsolidation'>;
  consolidation: Pick<DiffState, 'possibleDuplicates'>;
};

type PossibleDuplicates = DiffState['possibleDuplicates'];
type PossibleDuplicatesArgs = Parameters<typeof CandidatesService.getCandidateByIds>[number];

export const compareCandidatesThunk = createAsyncThunk<
  {
    diff: ReturnType<typeof compareSeveralCandidatesItemsMapper>;
    duplicates: ReturnType<typeof mapCandidateItemResponseToMap>;
  },
  {
    possibleDuplicatesIds: (string | number)[];
    targetCandidateData: CandidateItem;
    candidateIdForDetailsRequest?: string | number;
  }
>(
  'consolidation/compareCandidates',
  async ({ possibleDuplicatesIds, targetCandidateData, candidateIdForDetailsRequest }) => {
    const targetCandidateDataResponse = mapCandidateItemToCandidateItemResponse(targetCandidateData);
    const targetCandidateDataResponseWithNormalizedDates =
      normalizeDatesInCandidateItemResponse(targetCandidateDataResponse);
    const uniquePossibleDuplicatesIds = [...new Set(possibleDuplicatesIds.map((id) => id.toString()))];
    const candidateIdForDetailsRequestString = candidateIdForDetailsRequest?.toString();
    const idsForBatchRequest = uniquePossibleDuplicatesIds.filter((id) => id !== candidateIdForDetailsRequestString);
    const { data: candidateWithDetails } = candidateIdForDetailsRequest
      ? await CandidatesService.getCandidateById(Number(candidateIdForDetailsRequest))
      : { data: null };
    const { data: batchPossibleDuplicates } = idsForBatchRequest.length
      ? await CandidatesService.getCandidateByIds(idsForBatchRequest)
      : { data: [] };
    const possibleDuplicates = candidateWithDetails
      ? [candidateWithDetails, ...batchPossibleDuplicates]
      : batchPossibleDuplicates;
    const normalizedPossibleDuplicates = possibleDuplicates.map(normalizeCandidatePreofferInfoInCandidateItemResponse);
    const normalizedTargetCandidateData = normalizeCandidatePreofferInfoInCandidateItemResponse(
      targetCandidateDataResponseWithNormalizedDates,
    );

    const diff = compareSeveralCandidatesItemsMapper({
      targetCandidate: normalizedTargetCandidateData,
      possibleDuplicates: normalizedPossibleDuplicates,
    });
    const duplicateIdsFromQuery = new Set(uniquePossibleDuplicatesIds);
    const duplicates = mapCandidateItemResponseToMap(
      normalizedPossibleDuplicates.filter(({ id }) => id && duplicateIdsFromQuery.has(id.toString())),
    );
    return { diff, duplicates };
  },
);

export const getPossibleDuplicatesThunk: AsyncThunk<
  PossibleDuplicates,
  PossibleDuplicatesArgs,
  Record<string, never>
> = createAsyncThunk<PossibleDuplicates, PossibleDuplicatesArgs, Record<string, never>>(
  'consolidation/getPossibleDuplicates',
  async (args, { getState }) => {
    const { data } = await CandidatesService.getCandidateByIds(args);

    const state = getState() as GetPossibleDuplicatesState;
    const { candidateDuplicates } = state.candidates.candidatesConsolidation;
    // CandidateConsolidationRoot clears candidates state on batch navigation, while consolidation state is retained.
    const duplicateMetadataById = Object.keys(candidateDuplicates).length
      ? candidateDuplicates
      : state.consolidation.possibleDuplicates.items;

    const possibleDuplicates = data.map((candidate) => {
      const duplicateMetadata = candidate.id !== undefined ? duplicateMetadataById[candidate.id.toString()] : undefined;

      return {
        ...candidate,
        duplicateTriggerReasons: duplicateMetadata?.duplicateTriggerReasons,
        showNotDuplicateButton: duplicateMetadata?.showNotDuplicateButton,
      };
    });

    return possibleDuplicatesMapper(possibleDuplicates);
  },
);

export const mergeManyThunk = createAsyncThunk<
  ReturnType<Awaited<typeof ConsolidationService.mergeMany>>,
  Parameters<typeof ConsolidationService.mergeMany>[number]
>('consolidation/mergeMany', async (args) => {
  return ConsolidationService.mergeMany(args);
});
